import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

/**
 * AcceptedPayments — accepted-methods trust badge.
 * Sleek single-line horizontal strip of payment brands.
 *
 * Variants:
 *   • "block"    — boxed badge with title + arrow scroll controls (Trust / Checkout)
 *   • "row"      — continuous auto-scrolling marquee, no chrome (Footer)
 *   • "compact"  — small scrollable strip, no title (inline use)
 */

const BRANDS = [
  // W13 (2026-06-26): trimmed to the 8 methods the user wants in the ticker.
  // PayPal / Samsung Pay / Billie / Pay by Bank dropped per request; cards/
  // regional wallets dropped to keep the marquee tight. When you enable a new
  // method in Stripe, add it here (or via the W14 admin toggle UI when wired).
  // `id` matches routes/admin_payment_methods.py CATALOGUE so admin toggles work.
  { id: "amazon_pay",        label: "amazon pay",    bg: "#fff", color: "#232F3E", weight: 700 },
  { id: "link",              label: "Link",          bg: "#00D66F", color: "#000", weight: 700 },
  { id: "revolut_pay",       label: "Revolut",       bg: "#000", color: "#fff", weight: 800 },
  { id: "klarna",            label: "Klarna",        bg: "#FFB3C7", color: "#000", weight: 700 },
  { id: "afterpay_clearpay", label: "Clearpay",      bg: "#B2FCE4", color: "#000", weight: 800 },
  { id: "bacs_debit",        label: "Bank transfer", bg: "#fff", color: "#0F172A", weight: 600 },
  { id: "apple_pay",         label: " Pay",          bg: "#000", color: "#fff", weight: 600 },
  { id: "google_pay",        label: "G Pay",         bg: "#fff", color: "#000", weight: 700 },
  // Off by default — flip on via the admin panel once activated in Stripe.
  { id: "paypal",            label: "PayPal",        bg: "#fff", color: "#003087", weight: 800, italic: true },
  { id: "samsung_pay",       label: "Samsung Pay",   bg: "#1428A0", color: "#fff", weight: 700 },
  { id: "billie",            label: "Billie",        bg: "#fff", color: "#000", weight: 800 },
  { id: "pay_by_bank",       label: "Pay by Bank",   bg: "#fff", color: "#0F172A", weight: 700 },
];

const BrandChip = ({ b }) => (
  <span
    className="inline-flex shrink-0 items-center justify-center h-6 px-2 rounded-md text-[10px] tracking-tight shadow-sm"
    style={{
      background: b.bg,
      color: b.color,
      fontWeight: b.weight,
      fontStyle: b.italic ? "italic" : "normal",
      border: b.bg === "#fff" ? "1px solid #e5e7eb" : "none",
      letterSpacing: b.label.length > 4 ? "-0.01em" : "0.02em",
    }}
  >
    {b.dot ? (
      <span className="inline-flex items-center">
        <span className="h-3 w-3 rounded-full" style={{ background: b.color }} />
        <span className="h-3 w-3 rounded-full -ml-1.5" style={{ background: b.dot, mixBlendMode: "multiply" }} />
      </span>
    ) : (
      b.label
    )}
  </span>
);

// Edge fade overlay
const EdgeFade = ({ side }) => (
  <div
    aria-hidden
    className={`pointer-events-none absolute top-0 bottom-0 w-8 ${side === "left" ? "left-0 bg-gradient-to-r" : "right-0 bg-gradient-to-l"} from-white to-transparent z-[1]`}
  />
);

export default function AcceptedPayments({ variant = "block", className = "" }) {
  // W14 — runtime visibility filter pulled from /api/payment-methods/visible.
  // Brands without an `id` fall through unfiltered (backwards compat).
  const [allowed, setAllowed] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api
      .get("/payment-methods/visible")
      .then(({ data }) => {
        if (cancelled) return;
        const ids = new Set(
          (data?.methods || []).filter((m) => m.in_ticker).map((m) => m.id)
        );
        setAllowed(ids);
      })
      .catch(() => {
        if (!cancelled) setAllowed(new Set(BRANDS.map((b) => b.id)));
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const visibleBrands =
    allowed === null
      ? BRANDS.filter((b) => !b.id || true) // first render: show all hardcoded
      : BRANDS.filter((b) => !b.id || allowed.has(b.id));

  // Continuous marquee (footer)
  if (variant === "row") {
    return (
      <div
        className={`relative overflow-hidden ${className}`}
        data-testid="accepted-payments-row"
        aria-label="Accepted payment methods"
      >
        <EdgeFade side="left" />
        <EdgeFade side="right" />
        <div className="flex gap-1.5 animate-marquee whitespace-nowrap py-1">
          {[...visibleBrands, ...visibleBrands].map((b, i) => (
            <BrandChip key={i} b={b} />
          ))}
        </div>
      </div>
    );
  }

  // Sleek auto-marquee strip with subtle fade edges (no arrows — runs itself).
  // Pauses on hover (see `.animate-marquee:hover` in index.css) so users can
  // still read the brand names. Respects prefers-reduced-motion.
  return (
    <div
      className={`rounded-xl border border-stone-200 bg-white px-3 py-2.5 ${className}`}
      data-testid="accepted-payments-block"
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold text-emerald-700">
          <ShieldCheck className="h-3 w-3" /> Verified by Stripe · 24+ methods
        </span>
        <span className="text-[10px] uppercase tracking-wide text-stone-400">
          hover to pause
        </span>
      </div>
      <div className="relative overflow-hidden" aria-label="Accepted payment methods">
        <EdgeFade side="left" />
        <EdgeFade side="right" />
        <div className="flex gap-1.5 animate-marquee whitespace-nowrap py-0.5">
          {[...visibleBrands, ...visibleBrands].map((b, i) => (
            <BrandChip key={i} b={b} />
          ))}
        </div>
      </div>
    </div>
  );
}
